import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, PauseCircle, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MultiSelect } from "@/components/forms/MultiSelect";
import { Combobox } from "@/components/forms/Combobox";
import { useReferences } from "@/hooks/useReferences";
import { useAuth } from "@/context/AuthContext";

// Otter Assist V1 Phase 7 + Phase 8 — the ONE Otter Assist Preferences implementation (OAD-089).
// Rendered from BOTH Account Settings → Preferences and Engagement → Otter Assist → Preferences,
// reading and writing the SAME canonical backend record (OAD-096). There is no second store, and
// `users.notification_prefs` never gates Otter Assist.
//
// Only settings that actually take effect are shown: the notification and digest controls appear
// only when the backend reports the matching capability, so a control that does nothing is never
// displayed.

const SCOPE_COPY = {
  network: "My Network only",
  extended: "My Network + Extended Network",
  community: "My Network + Extended + Eligible Community",
};
const IDENTITY_COPY = { personal: "People", business: "Businesses", capital: "Capital" };
const LIMIT_COPY = { 5: "5 a day", 10: "10 a day", 15: "15 a day", 25: "25 a day" };
const SLOT_COPY = {
  "08:00": "8:00 AM", "11:00": "11:00 AM", "14:00": "2:00 PM", "17:00": "5:00 PM",
  "20:00": "8:00 PM",
};
const PAUSE_OPTIONS = [
  { days: 1, label: "1 day" }, { days: 3, label: "3 days" }, { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
];

const limitKey = (value) => (value === null || value === undefined ? "none" : String(value));

export function OtterAssistPreferences({ onSaved }) {
  const qc = useQueryClient();
  const { references } = useReferences();
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["oa-preferences"],
    queryFn: async () => (await api.get("/otter-assist/preferences")).data,
  });

  useEffect(() => { if (data?.preferences) setForm({ ...data.preferences }); }, [data]);

  const industryOptions = useMemo(
    () => (references.industries || []).map((i) => ({ value: i.value, label: i.label })),
    [references.industries]);
  const regionOptions = useMemo(() => [
    ...(references.us_states || []).map((s) => ({ value: s.label, label: s.label })),
    ...(references.countries || []).map((c) => ({ value: c.label, label: c.label })),
  ], [references.us_states, references.countries]);
  // The reference rows store a slug value with the real IANA name as the label, and the backend
  // allowlist only accepts a genuine IANA identifier (OAD-069), so the label is the value here.
  const tzOptions = useMemo(
    () => (references.timezones || []).map((t) => ({ value: t.label, label: t.label })),
    [references.timezones]);
  const accountZoneLabel = user?.timezone
    ? `Use my account time zone (${user.timezone})`
    : "Use my account time zone";

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // OAD-089 — the backend tells us which controls can actually take effect.
  const canNotify = !!data?.capabilities?.notifications;
  const canDigest = !!data?.capabilities?.email_digests;
  const slotOptions = useMemo(
    () => data?.options?.digest_slots || ["08:00", "11:00", "14:00", "17:00", "20:00"],
    [data?.options?.digest_slots]);
  const maxSlots = data?.options?.max_digest_slots || 5;

  const toggleSlot = (slot, on) => {
    const current = (form.digest_slots || []).filter((s) => slotOptions.includes(s));
    let next;
    if (on) {
      if (current.length >= maxSlots) {
        toast.error(`You can choose up to ${maxSlots} digests a day.`);
        return;
      }
      next = slotOptions.filter((s) => current.includes(s) || s === slot);
    } else {
      if (current.length <= 1) {
        toast.error("Keep at least one digest time, or turn email digests off.");
        return;
      }
      next = current.filter((s) => s !== slot);
    }
    set({ digest_slots: next });
    // The ONLY caller that may store a schedule: the member just moved a digest-time control.
    save({ digest_slots: next }, { slotsExplicit: true });
  };

  const toggleIdentity = (kind, on) => {
    const current = new Set(form.allowed_identities || []);
    if (on) current.add(kind); else current.delete(kind);
    set({ allowed_identities: Array.from(current) });
  };

  // `slotsExplicit` is passed per call rather than held in component state, so there is no
  // dirty flag that could survive a save, a cancel, a reload or a server refresh and later
  // convert an inherited schedule into an explicit one (OAD-127). Omitting `digest_slots`
  // from the body leaves any stored value untouched — the PUT is a partial update.
  const save = async (override = {}, { slotsExplicit = false } = {}) => {
    const next = { ...form, ...override };
    setSaving(true);
    try {
      const clear = [];
      const body = {
        receipt_enabled: !!next.receipt_enabled,
        audience_scope: next.audience_scope,
        allowed_identities: next.allowed_identities || [],
        preferred_industries: next.preferred_industries || [],
        preferred_regions: next.preferred_regions || [],
        clear,
      };
      if (next.daily_limit === null || next.daily_limit === undefined) clear.push("daily_limit");
      else body.daily_limit = next.daily_limit;
      if (next.paused_until) body.paused_until = next.paused_until; else clear.push("paused_until");
      if (next.timezone) body.timezone = next.timezone; else clear.push("timezone");
      if (canNotify) body.in_app_enabled = !!next.in_app_enabled;
      if (canNotify) body.sender_offer_email = !!next.sender_offer_email;
      if (canDigest) {
        // Turning email digests on or off is a SEPARATE setting: it never stores a schedule,
        // so switching off and back on leaves an inherited schedule inherited.
        body.email_digests_enabled = !!next.email_digests_enabled;
        const slots = (next.digest_slots || []).filter((s) => slotOptions.includes(s));
        if (slotsExplicit && slots.length) body.digest_slots = slots;
      }
      // `daily_limit: null` means "No Daily Limit" and is a real stored value, not a reset.
      if (clear.includes("daily_limit")) {
        clear.splice(clear.indexOf("daily_limit"), 1);
        body.daily_limit = null;
      }
      const { data: fresh } = await api.put("/otter-assist/preferences", body);
      setForm({ ...fresh.preferences });
      qc.setQueryData(["oa-preferences"], fresh);
      toast.success("Otter Assist preferences saved");
      if (onSaved) onSaved(fresh.preferences);
    } catch (e) {
      toast.error("Something went wrong with Otter Assist. Please try again.");
    } finally { setSaving(false); }
  };

  const pauseFor = (days) => {
    const until = new Date(Date.now() + days * 86400000).toISOString();
    set({ paused_until: until });
    save({ paused_until: until });
  };

  if (isLoading || !form) {
    return (
      <div className="flex justify-center py-10" data-testid="oa-prefs-loading">
        {isError ? (
          <p className="text-sm text-muted-foreground" data-testid="oa-prefs-error">
            Something went wrong with Otter Assist. Please try again.
          </p>
        ) : <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
      </div>
    );
  }

  const paused = form.paused_until && new Date(form.paused_until) > new Date();

  return (
    <div className="space-y-5" data-testid="oa-preferences">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Label className="font-[560] text-foreground" htmlFor="oa-receipt">
            Receive Otter Assist matches
          </Label>
          <p className="mt-1 text-xs text-muted-foreground">
            Turning this off stops new Community Needs from reaching you. Everything already in your
            inbox and your history is kept.
          </p>
        </div>
        <Switch id="oa-receipt" checked={!!form.receipt_enabled}
          onCheckedChange={(v) => { set({ receipt_enabled: v }); save({ receipt_enabled: v }); }}
          data-testid="oa-prefs-receipt" />
      </div>

      <Separator />

      <div>
        <Label className="font-[560] text-foreground">Who can reach you</Label>
        <p className="mt-1 text-xs text-muted-foreground">
          This is a real limit on who Otter Assist may match you with, not just a preference.
        </p>
        <Select value={form.audience_scope} onValueChange={(v) => set({ audience_scope: v })}>
          <SelectTrigger className="mt-2 max-w-md" data-testid="oa-prefs-scope">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(data?.options?.audience_scopes || []).map((s) => (
              <SelectItem key={s} value={s} data-testid={`oa-prefs-scope-${s}`}>
                {SCOPE_COPY[s] || s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="font-[560] text-foreground">Needs posted as</Label>
        <p className="mt-1 text-xs text-muted-foreground">
          Turning one off means you will not receive needs posted with that kind of profile.
        </p>
        <div className="mt-2 space-y-2.5">
          {(data?.options?.identities || []).map((kind) => (
            <div key={kind} className="flex items-center justify-between max-w-md">
              <Label className="font-[440] text-foreground" htmlFor={`oa-identity-${kind}`}>
                {IDENTITY_COPY[kind] || kind}
              </Label>
              <Switch id={`oa-identity-${kind}`}
                checked={(form.allowed_identities || []).includes(kind)}
                onCheckedChange={(v) => toggleIdentity(kind, v)}
                data-testid={`oa-prefs-identity-${kind}`} />
            </div>
          ))}
        </div>
      </div>

      <div>
        <Label className="font-[560] text-foreground">Daily Community Needs limit</Label>
        <p className="mt-1 text-xs text-muted-foreground">
          Counted against your own local calendar day. Anything over the limit waits its turn instead
          of being dropped.
        </p>
        <Select value={limitKey(form.daily_limit)}
          onValueChange={(v) => set({ daily_limit: v === "none" ? null : Number(v) })}>
          <SelectTrigger className="mt-2 max-w-xs" data-testid="oa-prefs-daily-limit">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(data?.options?.daily_limits || []).map((l) => (
              <SelectItem key={limitKey(l)} value={limitKey(l)}
                data-testid={`oa-prefs-daily-limit-${limitKey(l)}`}>
                {l === null ? "No daily limit" : LIMIT_COPY[l] || `${l} a day`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="font-[560] text-foreground">Your time zone</Label>
        <p className="mt-1 text-xs text-muted-foreground">
          Used for your daily limit and pause. We never guess it from your location.
        </p>
        <div className="mt-2 max-w-md">
          <Combobox
            options={[{ value: "auto", label: accountZoneLabel }, ...tzOptions]}
            value={form.timezone || "auto"}
            onChange={(v) => set({ timezone: v === "auto" ? null : v })}
            placeholder="Select a time zone"
            searchPlaceholder="Search time zones…"
            emptyText="No time zone found."
            testId="oa-prefs-timezone"
          />
        </div>
      </div>

      <Separator />

      <div>
        <Label className="font-[560] text-foreground">Pause Otter Assist</Label>
        <p className="mt-1 text-xs text-muted-foreground" data-testid="oa-prefs-pause-state">
          {paused
            ? `Paused until ${new Date(form.paused_until).toLocaleString()} — it resumes on its own.`
            : "Not paused. A pause always ends by itself; nothing is lost while it lasts."}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {PAUSE_OPTIONS.map((o) => (
            <Button key={o.days} type="button" size="sm" variant="outline" className="h-8"
              disabled={saving} onClick={() => pauseFor(o.days)}
              data-testid={`oa-prefs-pause-${o.days}`}>
              <PauseCircle className="mr-1.5 h-3.5 w-3.5" /> {o.label}
            </Button>
          ))}
          {paused && (
            <Button type="button" size="sm" className="h-8" disabled={saving}
              onClick={() => { set({ paused_until: null }); save({ paused_until: null }); }}
              data-testid="oa-prefs-resume">
              <PlayCircle className="mr-1.5 h-3.5 w-3.5" /> Resume now
            </Button>
          )}
        </div>
      </div>

      <Separator />

      <div>
        <Label className="font-[560] text-foreground">Preferred industries</Label>
        <p className="mt-1 text-xs text-muted-foreground">
          A nudge, not a filter — these needs rank higher for you, and nothing is excluded.
        </p>
        <div className="mt-2 max-w-md">
          <MultiSelect options={industryOptions} value={form.preferred_industries || []}
            onChange={(v) => set({ preferred_industries: v })} placeholder="Any industry"
            testId="oa-prefs-industries" />
        </div>
      </div>

      <div>
        <Label className="font-[560] text-foreground">Preferred regions</Label>
        <p className="mt-1 text-xs text-muted-foreground">
          Also only a nudge. You will still see matching needs from anywhere.
        </p>
        <div className="mt-2 max-w-md">
          <MultiSelect options={regionOptions} value={form.preferred_regions || []}
            onChange={(v) => set({ preferred_regions: v })} placeholder="Anywhere"
            testId="oa-prefs-regions" />
        </div>
      </div>

      {(canNotify || canDigest) && <Separator />}

      {canNotify && (
        <div className="space-y-4" data-testid="oa-prefs-notifications">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <Label className="font-[560] text-foreground" htmlFor="oa-in-app">
                In-app notifications
              </Label>
              <p className="mt-1 text-xs text-muted-foreground">
                A notification when a new Community Need reaches you. Turning this off never stops
                needs arriving, and your New badge keeps working.
              </p>
            </div>
            <Switch id="oa-in-app" checked={form.in_app_enabled !== false}
              onCheckedChange={(v) => { set({ in_app_enabled: v }); save({ in_app_enabled: v }); }}
              data-testid="oa-prefs-in-app" />
          </div>

          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <Label className="font-[560] text-foreground" htmlFor="oa-offer-email">
                Email me when someone offers to help
              </Label>
              <p className="mt-1 text-xs text-muted-foreground">
                For the needs you post. You always get the in-app notification either way.
              </p>
            </div>
            <Switch id="oa-offer-email" checked={!!form.sender_offer_email}
              onCheckedChange={(v) => {
                set({ sender_offer_email: v }); save({ sender_offer_email: v });
              }}
              data-testid="oa-prefs-offer-email" />
          </div>
        </div>
      )}

      {canDigest && (
        <div className="space-y-3" data-testid="oa-prefs-digests">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <Label className="font-[560] text-foreground" htmlFor="oa-digest">
                Email digests
              </Label>
              <p className="mt-1 text-xs text-muted-foreground">
                A single email with your newest matches. We never send an empty digest, and never
                more than 15 needs by email in one day.
              </p>
            </div>
            <Switch id="oa-digest" checked={form.email_digests_enabled !== false}
              onCheckedChange={(v) => {
                set({ email_digests_enabled: v }); save({ email_digests_enabled: v });
              }}
              data-testid="oa-prefs-digest-enabled" />
          </div>

          {form.email_digests_enabled !== false && (
            <div>
              <Label className="font-[560] text-foreground">Digest times</Label>
              <p className="mt-1 text-xs text-muted-foreground">
                Choose 1 to {maxSlots} times a day, in your own time zone.
              </p>
              <p className="mt-1 text-xs text-muted-foreground"
                 data-testid="oa-prefs-digest-schedule-origin">
                {form.digest_slots_explicit
                  ? "You’ve customized these times. Adjust any of them below."
                  : (form.digest_slots || []).length > 1
                    ? "You’re on our recommended schedule. Change any time below to make it your own."
                    : "You’re on our recommended schedule. Add your time zone in Account Settings to unlock all five times."}
              </p>
              <div className="mt-2 space-y-2.5">
                {slotOptions.map((slot) => (
                  <div key={slot} className="flex max-w-md items-center justify-between">
                    <Label className="font-[440] text-foreground" htmlFor={`oa-slot-${slot}`}>
                      {SLOT_COPY[slot] || slot}
                    </Label>
                    <Switch id={`oa-slot-${slot}`}
                      checked={(form.digest_slots || []).includes(slot)}
                      onCheckedChange={(v) => toggleSlot(slot, v)}
                      data-testid={`oa-prefs-digest-slot-${slot.replace(":", "")}`} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-3 pt-1">
        <Button type="button" onClick={() => save()} disabled={saving}
          data-testid="oa-prefs-save">
          {saving ? "Saving…" : "Save preferences"}
        </Button>
      </div>
    </div>
  );
}export default OtterAssistPreferences;
